import { BALANCE } from '../balance';

/**
 * The four opponents.
 *
 * One vocabulary shared by the mode-select screen, the join options, the room
 * and the bot itself, so a difficulty is a value that travels rather than a
 * string each layer re-invents.
 *
 * Ordered weakest to strongest, and the order is meaningful: the mode screen
 * renders them in it, and `botDifficultyFor` walks it.
 */
export const BOT_DIFFICULTIES = ['easy', 'medium', 'hard', 'insane'] as const;

export type BotDifficulty = (typeof BOT_DIFFICULTIES)[number];

export interface BotDifficultySettings {
  readonly moveIntervalMs: number;
  readonly blunderChance: number;
  readonly searchDepth: number;
}

export function isBotDifficulty(value: unknown): value is BotDifficulty {
  return typeof value === 'string' && (BOT_DIFFICULTIES as readonly string[]).includes(value);
}

/**
 * Read a difficulty off something a client sent.
 *
 * Falls back rather than throwing, because this arrives in join options: an
 * unknown string is a stale client or a typed URL, and neither is worth
 * refusing a match over. Nothing here is authority-sensitive — a player who
 * forges this picks their own opponent's strength, which is exactly what the
 * screen lets them do anyway.
 */
export function parseBotDifficulty(value: unknown): BotDifficulty {
  return isBotDifficulty(value) ? value : (BALANCE.bot.defaultDifficulty as BotDifficulty);
}

export function botDifficultySettings(difficulty: BotDifficulty): BotDifficultySettings {
  return BALANCE.bot.difficulties[difficulty];
}

/**
 * The name the opponent panel shows.
 *
 * Kept inside `nicknameMaxLength`, which is where `PlayerPanel` truncates — the
 * longest of these is `CPU-INSANE` at exactly ten. It says CPU on purpose: a
 * bot filling an empty queue must not be mistaken for a person.
 */
export function botDisplayName(difficulty: BotDifficulty): string {
  return `CPU-${difficulty.toUpperCase()}`;
}
